import { useCallback } from 'react';
import { useFetchGuidingChecklists, useTickChecklistItem } from './useGuidingChecklists';

/**
 * Tick a checklist item the user has just satisfied by CREATING a record
 * rather than importing a file.
 *
 * The backend auto-ticks `upload-suppliers` and `upload-installations` off the
 * bulk import endpoints only (`POST /suppliers/import`, `POST
 * /installations/import` — docs/features/guiding-checklists/README.md, the
 * auto-tick table). Anything that creates the same records another way leaves
 * the step looking untouched.
 *
 * The EU Communication template is exactly that case, and the guided path now
 * offers it for both steps: its Upload hub flow matches-or-CREATES the supplier
 * (`POST /suppliers/supplier`), then its installation (`POST
 * /installations/add`), then posts the emissions — so only the emissions step
 * ticked, and a declarant who had just added both was told they hadn't.
 *
 * This is a client-side patch over that gap and it only covers records this app
 * creates; the durable fix is an auto-tick trigger on those two create
 * endpoints, which lives in the backend repo. Until then: fire-and-forget,
 * idempotent server-side, and silent unless a live checklist is still waiting
 * on the item — so it costs nothing for operators or for anyone past onboarding.
 */
export const useTickOnRecordCreated = (itemKey) => {
  // Relaxed against the panel's own observer (staleTime 0): the floating widget
  // keeps this query warm app-wide, so this is a cache read rather than another
  // request from every page that happens to create records.
  const { data: checklists } = useFetchGuidingChecklists({
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const tickMutation = useTickChecklistItem();

  return useCallback(() => {
    const waiting = checklists?.find(
      (checklist) =>
        !checklist.dismissed &&
        checklist.items?.some((item) => item.key === itemKey && !item.completed),
    );
    if (waiting) tickMutation.mutate({ checklistSlug: waiting.slug, itemKey });
  }, [checklists, itemKey, tickMutation]);
};

export default useTickOnRecordCreated;
